import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API, { getImageUrl } from '../../utils/api';

// ===== TOAST =====
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`toast ${type}`}>
      <span>{type === 'success' ? '✓' : type === 'error' ? '✗' : 'ℹ'}</span>
      {msg}
    </div>
  );
};

// ===== BLOG MODAL =====
const BlogModal = ({ blog, onClose, onSave, toast }) => {
  const [form, setForm] = useState({
    page: blog?.page || 'home',
    title: blog?.title || '',
    excerpt: blog?.excerpt || '',
    content: blog?.content || '',
    author: blog?.author || 'The Village Investors',
    tags: blog?.tags?.join(', ') || '',
    importantLinks: blog?.importantLinks || [],
    featured: blog?.featured || false,
    isActive: blog?.isActive !== false,
    order: blog?.order || 0,
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(blog?.image ? getImageUrl(blog.image) : null);
  const [linkInput, setLinkInput] = useState({ label: '', url: '' });
  const [saving, setSaving] = useState(false);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (file) { setImage(file); setImagePreview(URL.createObjectURL(file)); }
  };

  const addLink = () => {
    if (linkInput.url) {
      setForm(f => ({ ...f, importantLinks: [...f.importantLinks, { ...linkInput }] }));
      setLinkInput({ label: '', url: '' });
    }
  };

  const removeLink = (i) => setForm(f => ({ ...f, importantLinks: f.importantLinks.filter((_, idx) => idx !== i) }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => {
        if (k === 'importantLinks') fd.append(k, JSON.stringify(form[k]));
        else fd.append(k, form[k]);
      });
      if (image) fd.append('image', image);
      if (blog?._id) await API.put(`/blogs/${blog._id}`, fd);
      else await API.post('/blogs', fd);
      toast('Blog saved successfully!', 'success');
      onSave();
    } catch { toast('Error saving blog', 'error'); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{blog ? 'Edit Article' : 'New Article'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Page</label>
              <select value={form.page} onChange={e => setForm({...form, page: e.target.value})}>
                {['home','yourself','earth','stocks','generosity'].map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Author</label>
              <input value={form.author} onChange={e => setForm({...form, author: e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
          </div>
          <div className="form-group">
            <label>Excerpt *</label>
            <textarea rows="3" value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Content (HTML supported)</label>
            <textarea rows="8" value={form.content} onChange={e => setForm({...form, content: e.target.value})}
              placeholder="<p>Your article content here...</p>" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="investing, growth" />
            </div>
            <div className="form-group">
              <label>Order</label>
              <input type="number" value={form.order} onChange={e => setForm({...form, order: Number(e.target.value)})} />
            </div>
          </div>
          <div className="form-group">
            <label>Cover Image</label>
            <input type="file" accept="image/*" onChange={handleImage} />
            {imagePreview && <img src={imagePreview} alt="Preview" className="img-preview" />}
          </div>
          <div className="form-group">
            <label>Important Links</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 8 }}>
              <input placeholder="Label" value={linkInput.label} onChange={e => setLinkInput({...linkInput, label: e.target.value})} />
              <input placeholder="https://..." value={linkInput.url} onChange={e => setLinkInput({...linkInput, url: e.target.value})} />
              <button className="btn-gold" style={{ padding: '10px 14px', whiteSpace: 'nowrap' }} onClick={addLink} type="button">+ Add</button>
            </div>
            {form.importantLinks.map((link, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: 'var(--cream)', marginBottom: 6,
                border: '1px solid var(--light-border)', fontSize: '0.85rem'
              }}>
                <span>{link.label || link.url}</span>
                <button onClick={() => removeLink(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', fontSize: '1rem' }}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
              Active (Visible on site)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} />
              Featured
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-gold" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : blog ? 'Update Article' : 'Create Article'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== VIDEO MODAL (Admin) =====
const VideoModal = ({ video, onClose, onSave, toast }) => {
  const [form, setForm] = useState({
    page: video?.page || 'all',
    title: video?.title || '',
    description: video?.description || '',
    url: video?.url || '',
    thumbnail: video?.thumbnail || '',
    featured: video?.featured || false,
    isActive: video?.isActive !== false,
    order: video?.order || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title || !form.url) { toast('Title and URL are required', 'error'); return; }
    setSaving(true);
    try {
      if (video?._id) await API.put(`/videos/${video._id}`, form);
      else await API.post('/videos', form);
      toast('Video saved!', 'success');
      onSave();
    } catch { toast('Error saving video', 'error'); }
    setSaving(false);
  };

  const detectPlatform = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return '▶ YouTube';
    if (url.includes('vimeo.com')) return '▶ Vimeo';
    if (url.match(/\.(mp4|webm|ogg)$/i)) return '▶ Direct Video';
    return '';
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{video ? 'Edit Video' : 'Add Video'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>Section / Page</label>
              <select value={form.page} onChange={e => setForm({...form, page: e.target.value})}>
                <option value="all">All Pages</option>
                {['home','yourself','earth','stocks','generosity'].map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Order</label>
              <input type="number" value={form.order} onChange={e => setForm({...form, order: Number(e.target.value)})} />
            </div>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Video title" />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description of this video..." />
          </div>

          <div className="form-group">
            <label>Video URL *</label>
            <input
              value={form.url}
              onChange={e => setForm({...form, url: e.target.value})}
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
            />
            {form.url && (
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--forest)', fontWeight: 500 }}>
                {detectPlatform(form.url) || '🔗 Custom URL'}
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--warm-gray)' }}>
              Supports: YouTube, Vimeo, or direct MP4/WebM links
            </div>
          </div>

          <div className="form-group">
            <label>Custom Thumbnail URL <span style={{ fontWeight: 400, color: 'var(--warm-gray)' }}>(optional — auto-detected for YouTube)</span></label>
            <input
              value={form.thumbnail}
              onChange={e => setForm({...form, thumbnail: e.target.value})}
              placeholder="https://... (leave blank for auto)"
            />
            {form.thumbnail && (
              <img src={form.thumbnail} alt="Thumbnail preview" className="img-preview" onError={e => e.target.style.display='none'} />
            )}
          </div>

          <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
              Active (Visible on site)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
              <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} />
              Featured
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-gold" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : video ? 'Update Video' : 'Add Video'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== BANNER MODAL =====
const BannerModal = ({ banner, onClose, onSave, toast }) => {
  const [form, setForm] = useState({
    page: banner?.page || 'home',
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    ctaText: banner?.ctaText || '',
    ctaLink: banner?.ctaLink || '',
    order: banner?.order || 0,
    isActive: banner?.isActive !== false,
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(banner?.image ? getImageUrl(banner.image) : null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title || (!banner && !image)) { toast('Title and image are required', 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => fd.append(k, form[k]));
      if (image) fd.append('image', image);
      if (banner?._id) await API.put(`/banners/${banner._id}`, fd);
      else await API.post('/banners', fd);
      toast('Banner saved!', 'success');
      onSave();
    } catch { toast('Error saving banner', 'error'); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{banner ? 'Edit Banner' : 'New Banner'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Page</label>
            <select value={form.page} onChange={e => setForm({...form, page: e.target.value})}>
              {['home','yourself','earth','stocks','generosity'].map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <textarea rows="2" value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label>CTA Button Text</label>
              <input value={form.ctaText} onChange={e => setForm({...form, ctaText: e.target.value})} placeholder="Learn More" />
            </div>
            <div className="form-group">
              <label>CTA Link</label>
              <input value={form.ctaLink} onChange={e => setForm({...form, ctaLink: e.target.value})} placeholder="/yourself or https://..." />
            </div>
          </div>
          <div className="form-group">
            <label>Order</label>
            <input type="number" value={form.order} onChange={e => setForm({...form, order: Number(e.target.value)})} />
          </div>
          <div className="form-group">
            <label>Hero Image {!banner && '*'}</label>
            <input type="file" accept="image/*" onChange={e => {
              const f = e.target.files[0];
              if (f) { setImage(f); setImagePreview(URL.createObjectURL(f)); }
            }} />
            {imagePreview && <img src={imagePreview} alt="Preview" className="img-preview" />}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
            Active (Visible on site)
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-gold" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : banner ? 'Update Banner' : 'Create Banner'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== BLOGS MANAGER =====
const BlogsManager = ({ toast }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const r = await API.get('/blogs');
    setBlogs(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteBlog = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await API.delete(`/blogs/${id}`);
    toast('Article deleted', 'info');
    load();
  };

  const filtered = filter === 'all' ? blogs : blogs.filter(b => b.page === filter);

  return (
    <div>
      <div className="admin-topbar">
        <h1>Articles</h1>
        <button className="btn-gold" onClick={() => setModal({ type: 'new' })}>+ New Article</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all','home','yourself','earth','stocks','generosity'].map(p => (
          <button key={p} className={filter === p ? 'btn-gold' : 'btn-outline'} style={{ padding: '8px 16px', fontSize: '0.82rem' }} onClick={() => setFilter(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <div className="admin-card">
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table className="admin-table">
            <thead>
              <tr><th>Title</th><th>Page</th><th>Author</th><th>Status</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(blog => (
                <tr key={blog._id}>
                  <td>
                    <strong style={{ fontFamily: 'Cormorant Garamond, serif' }}>{blog.title}</strong>
                    {blog.featured && <span className="badge badge-gold" style={{ marginLeft: 8 }}>Featured</span>}
                  </td>
                  <td><span className="badge badge-green">{blog.page}</span></td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--warm-gray)' }}>{blog.author}</td>
                  <td><span className={`badge ${blog.isActive ? 'badge-green' : 'badge-gray'}`}>{blog.isActive ? 'Active' : 'Hidden'}</span></td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--warm-gray)' }}>{new Date(blog.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-sm btn-edit" onClick={() => setModal({ type: 'edit', data: blog })}>Edit</button>
                      <button className="btn-sm btn-delete" onClick={() => deleteBlog(blog._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--warm-gray)' }}>No articles found</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {modal && <BlogModal blog={modal.data} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} toast={toast} />}
    </div>
  );
};

// ===== VIDEOS MANAGER =====
const VideosManager = ({ toast }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const r = await API.get('/videos');
    setVideos(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await API.delete(`/videos/${id}`);
    toast('Video deleted', 'info');
    load();
  };

  const filtered = filter === 'all' ? videos : videos.filter(v => v.page === filter);

  const getPlatformIcon = (url) => {
    if (!url) return '▶';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return '🔴';
    if (url.includes('vimeo.com')) return '🔵';
    return '▶';
  };

  return (
    <div>
      <div className="admin-topbar">
        <h1>Watch Videos</h1>
        <button className="btn-gold" onClick={() => setModal({ type: 'new' })}>+ Add Video</button>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, rgba(44,74,62,0.08), rgba(201,168,76,0.08))',
        border: '1px solid var(--light-border)', padding: '16px 20px', marginBottom: 24,
        fontSize: '0.88rem', color: 'var(--warm-gray)', lineHeight: 1.7
      }}>
        <strong style={{ color: 'var(--forest)' }}>📹 How Videos Work:</strong>
        {' '}Paste any YouTube, Vimeo, or direct video URL. Thumbnails are auto-detected for YouTube.
        Set the page to control where videos appear — choose "All Pages" to show in the dedicated Watch Videos section only.
        Videos appear on their assigned page section AND in the main Watch Videos tab.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all','home','yourself','earth','stocks','generosity'].map(p => (
          <button key={p} className={filter === p ? 'btn-gold' : 'btn-outline'} style={{ padding: '8px 16px', fontSize: '0.82rem' }} onClick={() => setFilter(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>

      <div className="admin-card">
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table className="admin-table">
            <thead>
              <tr><th>Platform</th><th>Title</th><th>Page</th><th>Status</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(video => (
                <tr key={video._id}>
                  <td style={{ fontSize: '1.3rem', textAlign: 'center' }}>{getPlatformIcon(video.url)}</td>
                  <td>
                    <strong style={{ fontFamily: 'Cormorant Garamond, serif' }}>{video.title}</strong>
                    {video.featured && <span className="badge badge-gold" style={{ marginLeft: 8 }}>Featured</span>}
                    <div style={{ fontSize: '0.75rem', color: 'var(--warm-gray)', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {video.url}
                    </div>
                  </td>
                  <td><span className="badge badge-green">{video.page}</span></td>
                  <td><span className={`badge ${video.isActive ? 'badge-green' : 'badge-gray'}`}>{video.isActive ? 'Active' : 'Hidden'}</span></td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--warm-gray)' }}>{new Date(video.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-sm btn-edit" onClick={() => setModal({ type: 'edit', data: video })}>Edit</button>
                      <button className="btn-sm btn-delete" onClick={() => deleteVideo(video._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--warm-gray)' }}>
                  No videos yet. Click "+ Add Video" to post your first video!
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {modal && <VideoModal video={modal.data} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} toast={toast} />}
    </div>
  );
};

// ===== BANNERS MANAGER =====
const BannersManager = ({ toast }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await API.get('/banners');
    setBanners(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteBanner = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    await API.delete(`/banners/${id}`);
    toast('Banner deleted', 'info');
    load();
  };

  return (
    <div>
      <div className="admin-topbar">
        <h1>Hero Banners</h1>
        <button className="btn-gold" onClick={() => setModal({ type: 'new' })}>+ New Banner</button>
      </div>
      <div className="admin-card">
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table className="admin-table">
            <thead>
              <tr><th>Image</th><th>Title</th><th>Page</th><th>Order</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {banners.map(banner => (
                <tr key={banner._id}>
                  <td>{banner.image && <img src={getImageUrl(banner.image)} alt="" style={{ width: 80, height: 50, objectFit: 'cover', border: '1px solid var(--light-border)' }} />}</td>
                  <td><strong style={{ fontFamily: 'Cormorant Garamond, serif' }}>{banner.title}</strong></td>
                  <td><span className="badge badge-gold">{banner.page}</span></td>
                  <td>{banner.order}</td>
                  <td><span className={`badge ${banner.isActive ? 'badge-green' : 'badge-gray'}`}>{banner.isActive ? 'Active' : 'Hidden'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-sm btn-edit" onClick={() => setModal({ type: 'edit', data: banner })}>Edit</button>
                      <button className="btn-sm btn-delete" onClick={() => deleteBanner(banner._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {banners.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--warm-gray)' }}>No banners yet. Create your first hero banner!</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {modal && <BannerModal banner={modal.data} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} toast={toast} />}
    </div>
  );
};

// ===== CONTACTS MANAGER =====
const ContactsManager = ({ toast }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await API.get('/contact');
    setContacts(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await API.put(`/contact/${id}/read`); load(); };
  const deleteContact = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await API.delete(`/contact/${id}`);
    toast('Message deleted', 'info');
    setSelected(null);
    load();
  };

  const unread = contacts.filter(c => !c.read).length;

  return (
    <div>
      <div className="admin-topbar">
        <h1>Contact Messages {unread > 0 && <span className="badge badge-red" style={{ fontSize: '1rem', marginLeft: 10 }}>{unread} new</span>}</h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
        <div className="admin-card">
          {loading ? <div className="loading"><div className="spinner" /></div> : (
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Subject</th><th>Status</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer', fontWeight: !c.read ? 600 : 400 }} onClick={() => { setSelected(c); if (!c.read) markRead(c._id); }}>
                    <td>{c.name}</td>
                    <td style={{ fontSize: '0.85rem' }}>{c.email}</td>
                    <td style={{ fontSize: '0.85rem' }}>{c.subject || '-'}</td>
                    <td><span className={`badge ${c.read ? 'badge-gray' : 'badge-red'}`}>{c.read ? 'Read' : 'New'}</span></td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--warm-gray)' }}>{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td onClick={e => e.stopPropagation()}><button className="btn-sm btn-delete" onClick={() => deleteContact(c._id)}>Delete</button></td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--warm-gray)' }}>No messages yet</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        {selected && (
          <div className="admin-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem' }}>Message Detail</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--warm-gray)' }}>From</strong><p>{selected.name} ({selected.email})</p></div>
              <div><strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--warm-gray)' }}>Subject</strong><p>{selected.subject || 'No subject'}</p></div>
              <div><strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--warm-gray)' }}>Message</strong>
                <p style={{ marginTop: 8, lineHeight: 1.8, padding: '16px', background: 'var(--cream)', whiteSpace: 'pre-wrap' }}>{selected.message}</p>
              </div>
              <div><strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--warm-gray)' }}>Received</strong><p>{new Date(selected.createdAt).toLocaleString()}</p></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== SETTINGS =====
const SettingsManager = ({ toast }) => {
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/settings/logo').then(r => {
      if (r.data?.value) setCurrentLogo(getImageUrl(r.data.value));
    }).catch(() => {});
  }, []);

  const uploadLogo = async () => {
    if (!logo) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('logo', logo);
      const r = await API.post('/settings/logo/upload', fd);
      setCurrentLogo(getImageUrl(r.data.value));
      setLogo(null);
      setLogoPreview(null);
      toast('Logo updated successfully!', 'success');
    } catch { toast('Error uploading logo', 'error'); }
    setSaving(false);
  };

  return (
    <div>
      <div className="admin-topbar"><h1>Settings</h1></div>
      <div className="admin-card" style={{ maxWidth: 600 }}>
        <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', marginBottom: 24 }}>Site Logo</h3>
        {currentLogo && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: '0.8rem', color: 'var(--warm-gray)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Current Logo</p>
            <div style={{ padding: 20, background: 'var(--cream)', display: 'inline-block', border: '1px solid var(--light-border)' }}>
              <img src={currentLogo} alt="Current Logo" className="logo-preview" style={{ maxHeight: 80, maxWidth: 240 }} />
            </div>
          </div>
        )}
        <div className="logo-upload-area" onClick={() => document.getElementById('logo-input').click()}>
          {logoPreview
            ? <img src={logoPreview} alt="New logo preview" className="logo-preview" />
            : (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: 8, color: 'var(--gold)' }}>⬆</div>
                <p style={{ color: 'var(--warm-gray)', fontSize: '0.9rem' }}>Click to upload new logo</p>
                <p style={{ color: 'var(--warm-gray)', fontSize: '0.78rem', marginTop: 4 }}>PNG, JPG, SVG up to 10MB</p>
              </div>
            )
          }
          <input id="logo-input" type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; if (f) { setLogo(f); setLogoPreview(URL.createObjectURL(f)); } }}
          />
        </div>
        {logo && (
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button className="btn-gold" onClick={uploadLogo} disabled={saving}>{saving ? 'Uploading...' : 'Upload Logo'}</button>
            <button className="btn-outline btn-sm" onClick={() => { setLogo(null); setLogoPreview(null); }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== DASHBOARD HOME =====
const DashboardHome = () => {
  const [stats, setStats] = useState({ blogs: 0, banners: 0, contacts: 0, unread: 0, videos: 0 });

  useEffect(() => {
    Promise.all([
      API.get('/blogs'),
      API.get('/banners'),
      API.get('/contact'),
      API.get('/videos'),
    ]).then(([blogs, banners, contacts, videos]) => {
      setStats({
        blogs: blogs.data.length,
        banners: banners.data.length,
        contacts: contacts.data.length,
        unread: contacts.data.filter(c => !c.read).length,
        videos: videos.data.length,
      });
    }).catch(() => {});
  }, []);

  return (
    <div>
      <div className="admin-topbar">
        <h1>Dashboard</h1>
        <a href="/" target="_blank" className="btn-outline btn-sm" style={{ textDecoration: 'none' }}>View Site ↗</a>
      </div>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {[
          { number: stats.blogs, label: 'Total Articles', icon: '✏' },
          { number: stats.videos, label: 'Videos', icon: '▶' },
          { number: stats.banners, label: 'Hero Banners', icon: '🖼' },
          { number: stats.contacts, label: 'Messages', icon: '✉' },
          { number: stats.unread, label: 'Unread', icon: '🔔' },
        ].map(s => (
          <div key={s.label} className="stat-widget">
            <div style={{ fontSize: '1.8rem' }}>{s.icon}</div>
            <div className="stat-widget-number">{s.number}</div>
            <div className="stat-widget-label">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="admin-card">
        <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem', marginBottom: 16 }}>Quick Start Guide</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { step: '1', title: 'Upload your logo', desc: 'Go to Settings to upload your brand logo', link: 'settings' },
            { step: '2', title: 'Add Hero Banners', desc: 'Create compelling hero banners for each page', link: 'banners' },
            { step: '3', title: 'Publish Articles', desc: 'Write and publish blogs for each section', link: 'blogs' },
            { step: '4', title: 'Post Videos', desc: 'Add YouTube, Vimeo or direct video links to the Watch Videos section', link: 'videos' },
          ].map(item => (
            <div key={item.step} style={{ display: 'flex', gap: 16, padding: '16px', background: 'var(--cream)', border: '1px solid var(--light-border)' }}>
              <div style={{ width: 36, height: 36, background: 'var(--forest)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem', flexShrink: 0 }}>{item.step}</div>
              <div>
                <strong style={{ display: 'block', marginBottom: 4 }}>{item.title}</strong>
                <p style={{ fontSize: '0.85rem', color: 'var(--warm-gray)' }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN ADMIN DASHBOARD =====
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const navItems = [
    { label: 'Dashboard', path: '/admin', icon: '◈' },
    { label: 'Hero Banners', path: '/admin/banners', icon: '🖼' },
    { label: 'Articles', path: '/admin/blogs', icon: '✏' },
    { label: 'Watch Videos', path: '/admin/videos', icon: '▶' },
    { label: 'Messages', path: '/admin/contacts', icon: '✉' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙' },
  ];

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>Village <span style={{ color: 'var(--gold)' }}>Investors</span></h2>
          <p>Admin Portal</p>
        </div>
        <nav className="admin-nav">
          <div className="admin-nav-section">Navigation</div>
          {navItems.map(item => (
            <button key={item.path} className={`admin-nav-item ${location.pathname === item.path ? 'active' : ''}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }}>
              <span style={{ fontSize: '1rem' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
          <div className="admin-nav-section" style={{ marginTop: 16 }}>Account</div>
          <div className="admin-nav-item" style={{ cursor: 'default', color: 'rgba(255,255,255,0.4)' }}>👤 {user?.username}</div>
          <button className="admin-nav-item" onClick={handleLogout} style={{ color: '#ef9a9a' }}>← Sign Out</button>
        </nav>
      </aside>

      <main className="admin-main">
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/banners" element={<BannersManager toast={showToast} />} />
          <Route path="/blogs" element={<BlogsManager toast={showToast} />} />
          <Route path="/videos" element={<VideosManager toast={showToast} />} />
          <Route path="/contacts" element={<ContactsManager toast={showToast} />} />
          <Route path="/settings" element={<SettingsManager toast={showToast} />} />
        </Routes>
      </main>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminDashboard;
